/**
 * Shared time constants for `apps/web`.
 *
 * PATTERN TO FOLLOW: any cache, debounce, or timeout duration used in more than
 * one place (or representing a product decision rather than a local detail)
 * belongs here, not as a loose `const` inside a component.
 *
 * Why: these values were redeclared in ~12 files using two spellings of the same
 * number (`5 * 60 * 1000` and `300_000`), so changing the cache policy required
 * touching every location and risked divergent values. Import from here instead
 * of redefining them.
 */

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Milliseconds in a minute. Base for deriving the remaining durations. */
const MINUTE_MS = MS_PER_SECOND * SECONDS_PER_MINUTE;

/** Minutes a cached datum is considered fresh (offline cache and session). */
const STALE_MINUTES = 5;

/**
 * Default `staleTime` for offline-first queries (PowerSync + TanStack).
 *
 * Data syncs locally through PowerSync, so a generous `staleTime` (5 min)
 * avoids redundant refetches/re-renders with no real risk of showing stale
 * data. It is also the global `queryClient` default.
 */
export const OFFLINE_CACHE_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * `staleTime` for session/authentication queries.
 *
 * It remains a separate constant (although today it matches the offline cache
 * value) because it is an independent decision: it must stay aligned across
 * `useSession`, the `_app` route guard, and the `session-bridge`.
 */
export const SESSION_STALE_TIME_MS = STALE_MINUTES * MINUTE_MS;

/**
 * Time ceiling for session checks by the route guards (`_app` and `_auth`).
 *
 * P23/CC-60 · I1-bis — MEASURED WITH A BROWSER (2026-08-02). This value lives
 * here, rather than as a local literal, because the bug was EXACTLY that:
 * `_app/route.tsx` had its own `const SESSION_CHECK_TIMEOUT = 1500`, while
 * `_auth/route.tsx` was left without it. With `get-session` STUCK (neither 401
 * nor 5xx: both resolve quickly), `_auth`'s `beforeLoad` never resolved, the
 * router did not mount the route, and `/login` kept an EMPTY `#root` at 3, 6,
 * 10, 15, and 20 s—the entire app blank, indefinitely—while `/` rendered fine.
 * That is why I1 was closed green: it measured the route that was healthy.
 *
 * If the ceiling ever needs adjustment, adjust it HERE, for both.
 */
export const SESSION_CHECK_TIMEOUT_MS = 1500;

/** Seconds before revoking a download's object URL (see constant). */
const OBJECT_URL_REVOKE_SECONDS = 10;

/**
 * Grace period before revoking a `blob:` object URL after triggering a download.
 *
 * It must be comfortably greater than the time the browser takes to consume
 * the `<a download>` href; revoking earlier would abort the download.
 */
export const OBJECT_URL_REVOKE_DELAY_MS = OBJECT_URL_REVOKE_SECONDS * MS_PER_SECOND;
